import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import type { ChartDataPoint } from './types';

const DEFAULT_COLOR = '#6366f1';

interface HorizontalBarChartProps {
  data: ChartDataPoint[];
  color?: string;
}

/**
 * Horizontal bar chart — useful when category labels are long.
 * Fills its parent container via `ResponsiveContainer`.
 *
 * @example
 * <HorizontalBarChart
 *   data={[{ name: 'Alice', value: 23 }, { name: 'Bob', value: 17 }]}
 *   color="#10b981"
 * />
 */
export function HorizontalBarChart({ data, color = DEFAULT_COLOR }: HorizontalBarChartProps) {
  const chartData = data.map((d) => ({ name: d.name, value: d.value }));

  return (
    <ResponsiveContainer width="100%" height="100%">
      <RechartsBarChart
        layout="vertical"
        data={chartData}
        margin={{ top: 4, right: 16, left: 0, bottom: 4 }}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          stroke="currentColor"
          strokeOpacity={0.1}
          horizontal={false}
        />
        <XAxis type="number" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
        <YAxis
          type="category"
          dataKey="name"
          tick={{ fontSize: 11 }}
          tickLine={false}
          axisLine={false}
          width={90}
        />
        <Tooltip
          formatter={(value) =>
            typeof value === 'number' ? value.toLocaleString() : String(value ?? '')
          }
        />
        <Bar dataKey="value" fill={color} radius={[0, 4, 4, 0]} />
      </RechartsBarChart>
    </ResponsiveContainer>
  );
}
